/**
 * ResultsScreen component with purposeful animations
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { computeOverallAccuracy, accuracyToGrade } from '../../utils/timing';
import { usePageLoadAnimation } from '../../hooks/useAnimation';

interface ResultsState {
  score: number;
  maxCombo: number;
  perfectCount: number;
  goodCount: number;
  missCount: number;
  trackTitle?: string;
  trackArtist?: string;
  bpm?: number;
}

const GRADE_CONFIG: Record<string, { color: string; label: string }> = {
  S:  { color: '#e8b86d', label: 'Exceptional' },
  'A+': { color: '#81b29a', label: 'Outstanding' },
  A:  { color: '#81b29a', label: 'Excellent' },
  B:  { color: '#f5a623', label: 'Very Good' },
  C:  { color: '#e07a5f', label: 'Good' },
  D:  { color: '#a8a29e', label: 'Fair' },
  F:  { color: '#78716c', label: 'Keep Practicing' },
};

export const ResultsScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const chartRef = useRef<HTMLCanvasElement>(null);
  const isLoaded = usePageLoadAnimation();
  
  const [animatedScore, setAnimatedScore] = useState(0);
  const [animatedAccuracy, setAnimatedAccuracy] = useState(0);

  const state = location.state as ResultsState | null;
  const score = state?.score ?? 0;
  const maxCombo = state?.maxCombo ?? 0;
  const perfectCount = state?.perfectCount ?? 0;
  const goodCount = state?.goodCount ?? 0;
  const missCount = state?.missCount ?? 0;
  const trackTitle = state?.trackTitle ?? 'Unknown Track';
  const trackArtist = state?.trackArtist ?? '';

  const accuracy = computeOverallAccuracy(perfectCount, goodCount, missCount);
  const grade = accuracyToGrade(accuracy);
  const gradeConfig = GRADE_CONFIG[grade] ?? { color: '#ffffff', label: '' };
  const total = perfectCount + goodCount + missCount;

  const rafRef = useRef<number>(0);

  // Animate score and accuracy on load
  useEffect(() => {
    if (!isLoaded) return;

    const duration = 1500;
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeProgress = 1 - Math.pow(1 - progress, 4);

      setAnimatedScore(Math.floor(easeProgress * score));
      setAnimatedAccuracy(parseFloat((easeProgress * accuracy).toFixed(1)));

      if (progress < 1) rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [isLoaded, score, accuracy]);

  // Draw chart
  useEffect(() => {
    const canvas = chartRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const W = canvas.width;
    const H = canvas.height;
    const BAR_WIDTH = 50;
    const MAX_HEIGHT = H - 50;

    ctx.clearRect(0, 0, W, H);

    const bars = [
      { label: 'PERFECT', count: perfectCount, color: '#81b29a' },
      { label: 'GOOD', count: goodCount, color: '#e8b86d' },
      { label: 'MISS', count: missCount, color: '#e07a5f' },
    ];

    const maxCount = Math.max(...bars.map((b) => b.count), 1);
    const spacing = (W - bars.length * BAR_WIDTH) / (bars.length + 1);

    bars.forEach((bar, i) => {
      const barH = Math.round((bar.count / maxCount) * MAX_HEIGHT);
      const x = spacing + i * (BAR_WIDTH + spacing);
      const y = H - 30 - barH;

      ctx.fillStyle = bar.color + 'cc';
      ctx.beginPath();
      ctx.roundRect(x, y, BAR_WIDTH, barH, [4, 4, 0, 0]);
      ctx.fill();

      ctx.shadowColor = bar.color;
      ctx.shadowBlur = 15;
      ctx.fill();
      ctx.shadowBlur = 0;

      ctx.fillStyle = bar.color;
      ctx.font = '600 16px "Space Grotesk", sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(String(bar.count), x + BAR_WIDTH / 2, y - 10);

      ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
      ctx.font = '500 11px "Space Grotesk", sans-serif';
      ctx.fillText(bar.label, x + BAR_WIDTH / 2, H - 8);
    });

    ctx.textAlign = 'left';
  }, [perfectCount, goodCount, missCount]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8 sm:p-8 bg-glow-center">
      <div className="w-full max-w-3xl">
        {/* Header */}
        <div 
          className="text-center mb-12"
          style={{ 
            opacity: isLoaded ? 1 : 0,
            transform: isLoaded ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.6s cubic-bezier(0.22, 1, 0.36, 1)'
          }}
        >
          <p className="text-amber-500 text-sm font-semibold tracking-widest uppercase mb-3">
            Practice Complete
          </p>
          <h1 className="font-display text-display-md text-stone-100 mb-2">{trackTitle}</h1>
          {trackArtist && <p className="text-stone-500">{trackArtist}</p>}
        </div>

        {/* Grade & Stats Row */}
        <div 
          className="flex flex-wrap items-center justify-center gap-x-8 gap-y-6 sm:gap-16 mb-10 sm:mb-12"
          style={{ 
            opacity: isLoaded ? 1 : 0,
            transform: isLoaded ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.6s cubic-bezier(0.22, 1, 0.36, 1) 100ms'
          }}
        >
          {/* Grade with pulse on reveal */}
          <div className="text-center">
            <p className="text-stone-500 text-xs uppercase tracking-widest mb-2">Grade</p>
            <div
              className="font-display text-7xl sm:text-8xl font-bold leading-none transition-transform duration-500"
              style={{ 
                color: gradeConfig.color,
                transform: isLoaded ? 'scale(1)' : 'scale(0.5)',
                transitionDelay: '200ms'
              }}
            >
              {grade}
            </div>
            <p className="text-stone-500 text-sm mt-2">{gradeConfig.label}</p>
          </div>

          {/* Animated Score */}
          <div className="text-center">
            <p className="text-stone-500 text-xs uppercase tracking-widest mb-2">Score</p>
            <div className="font-mono text-4xl sm:text-5xl font-bold text-amber-400">
              {animatedScore.toLocaleString()}
            </div>
          </div>

          {/* Animated Accuracy */}
          <div className="text-center">
            <p className="text-stone-500 text-xs uppercase tracking-widest mb-2">Accuracy</p>
            <div className="font-mono text-4xl sm:text-5xl font-bold text-coral-400">
              {animatedAccuracy.toFixed(1)}%
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div 
          className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mb-10"
          style={{ 
            opacity: isLoaded ? 1 : 0,
            transform: isLoaded ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.6s cubic-bezier(0.22, 1, 0.36, 1) 200ms'
          }}
        >
          <StatCard label="Max Combo" value={`×${maxCombo}`} color="#f5a623" delay={0} />
          <StatCard label="Total Notes" value={String(total)} color="#e7e5e4" delay={100} />
          <StatCard label="Perfect Rate" value={total > 0 ? `${Math.round((perfectCount / total) * 100)}%` : '0%'} color="#81b29a" delay={200} />
        </div>

        {/* Distribution Chart */}
        <div 
          className="card-editorial rounded-2xl p-8 mb-10"
          style={{ 
            opacity: isLoaded ? 1 : 0,
            transform: isLoaded ? 'scale(1)' : 'scale(0.95)',
            transition: 'all 0.6s cubic-bezier(0.22, 1, 0.36, 1) 300ms'
          }}
        >
          <h3 className="text-stone-200 font-semibold mb-6 text-center">Hit Distribution</h3>
          <canvas ref={chartRef} width={400} height={160} className="w-full block mx-auto" style={{ maxWidth: '400px' }} />
        </div>

        {/* Detailed Breakdown */}
        <div 
          className="flex justify-center gap-12 mb-12"
          style={{ 
            opacity: isLoaded ? 1 : 0,
            transition: 'opacity 0.6s cubic-bezier(0.22, 1, 0.36, 1) 400ms'
          }}
        >
          <HitStat label="PERFECT" count={perfectCount} color="#81b29a" delay={0} />
          <HitStat label="GOOD" count={goodCount} color="#e8b86d" delay={100} />
          <HitStat label="MISS" count={missCount} color="#e07a5f" delay={200} />
        </div>

        {/* Actions */}
        <div 
          className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center"
          style={{ 
            opacity: isLoaded ? 1 : 0,
            transform: isLoaded ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.6s cubic-bezier(0.22, 1, 0.36, 1) 500ms'
          }}
        >
          <button type="button" onClick={() => navigate('/')} className="btn-secondary">
            Back to Library
          </button>
          <button type="button" onClick={() => navigate(-1)} className="btn-primary">
            Practice Again
          </button>
        </div>
      </div>
    </div>
  );
};

// Animated stat card
function StatCard({ label, value, color, delay }: { label: string; value: string; color: string; delay: number }) {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 300 + delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <div 
      className="card-editorial rounded-xl p-5 text-center"
      style={{ 
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(10px)',
        transition: `all 0.5s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`
      }}
    >
      <p className="text-stone-500 text-xs uppercase tracking-widest mb-2">{label}</p>
      <p className="text-2xl font-bold font-mono" style={{ color }}>{value}</p>
    </div>
  );
}

// Animated hit stat
function HitStat({ label, count, color, delay }: { label: string; count: number; color: string; delay: number }) {
  const [displayCount, setDisplayCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const visibilityTimer = setTimeout(() => setIsVisible(true), 400 + delay);
    
    const animateTimer = setTimeout(() => {
      const duration = 800;
      const startTime = performance.now();
      
      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easeProgress = 1 - Math.pow(1 - progress, 4);
        setDisplayCount(Math.floor(easeProgress * count));
        
        if (progress < 1) requestAnimationFrame(animate);
        else setDisplayCount(count);
      };
      
      requestAnimationFrame(animate);
    }, 400 + delay);
    
    return () => {
      clearTimeout(visibilityTimer);
      clearTimeout(animateTimer);
    };
  }, [count, delay]);

  return (
    <div 
      className="text-center"
      style={{ 
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'scale(1)' : 'scale(0.9)',
        transition: `all 0.4s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`
      }}
    >
      <p style={{ color }} className="text-3xl font-bold font-mono mb-1">{displayCount}</p>
      <p className="text-stone-500 text-xs uppercase tracking-widest">{label}</p>
    </div>
  );
}
